/**
 * The shopper's search filters, one shape for every place they live: the
 * filter drawer's draft, the URL (/search and /browse/...), the search
 * context and the recent searches.
 *
 * In the URL the attribute filters (gender, age group, size, color, brand,
 * material) are comma lists, the form /api/search/listings and
 * /api/search/facets parse. Condition keeps its old forms (`itemCondition[]=3`
 * and `itemCondition=3,4`) so every saved link still opens.
 */
import { ATTRIBUTE_FILTER_VALUES } from './search-filters';
import { encodeTagFacets, parseTagFacets, tagFacetCount } from './tag-facet-param';

export const ATTRIBUTE_FILTER_KEYS = ['gender', 'ageGroup', 'size', 'color', 'brand', 'material'] as const;
export type AttributeFilterName = (typeof ATTRIBUTE_FILTER_KEYS)[number];

export interface FilterState {
  minPrice?: number;
  maxPrice?: number;
  itemCondition?: number[] | null;
  /** The search page's category group; a browse page's category comes from its route. */
  categoryGroupId?: number;
  sold?: boolean;
  shippable?: boolean;
  featured?: boolean;
  /** Deals Only / Quality Only (utils/deal-quality-filter.ts). */
  deals?: boolean;
  quality?: boolean;
  gender?: string[];
  ageGroup?: string[];
  size?: string[];
  color?: string[];
  brand?: string[];
  material?: string[];
  /** Tag and type filters as the `tf` param carries them (utils/tag-facet-param.ts). */
  tf?: string;
}

/** Every key a filter writes to a URL or a search context, so they can be cleared together. */
export const FILTER_URL_KEYS = [
  'minPrice', 'maxPrice', 'min', 'max', 'itemCondition', 'itemCondition[]', 'sold', 'shippable', 'featured', 'deals', 'quality', ...ATTRIBUTE_FILTER_KEYS, 'tf',
] as const;

type QueryValue = string | string[] | number | number[] | boolean | null | undefined;

const first = (v: QueryValue): string | undefined => {
  if (Array.isArray(v)) return v.length ? String(v[0]) : undefined;
  return v === undefined || v === null ? undefined : String(v);
};

const positiveNumber = (v: QueryValue): number | undefined => {
  const n = Number(first(v));
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const isTrue = (v: QueryValue): boolean => {
  const s = first(v);
  return s === 'true' || s === '1';
};

/** A list from a comma string, repeated params or an array; trimmed, de-duplicated, restricted to `allowed`. */
export function parseList(v: QueryValue, allowed?: readonly string[]): string[] | undefined {
  if (v === undefined || v === null || v === '') return undefined;
  const raw = Array.isArray(v) ? v.flatMap(x => String(x).split(',')) : String(v).split(',');
  const list = [...new Set(raw.map(x => x.trim()).filter(Boolean))].filter(x => !allowed || allowed.includes(x));
  return list.length ? list : undefined;
}

function parseConditions(query: Record<string, QueryValue>): number[] | null {
  const raw = query['itemCondition[]'] ?? query.itemCondition;
  if (raw === undefined || raw === null || raw === '') return null;
  const values = (Array.isArray(raw) ? raw : String(raw).split(',')).map(Number).filter(n => Number.isInteger(n) && n > 0);
  return values.length ? [...new Set(values)] : null;
}

/** The filters a URL query (or a search context) carries. Unknown values are dropped. */
export function filtersFromQuery(query: Record<string, QueryValue>): FilterState {
  const state: FilterState = {};
  const minPrice = positiveNumber(query.minPrice ?? query.min);
  const maxPrice = positiveNumber(query.maxPrice ?? query.max);
  if (minPrice !== undefined) state.minPrice = minPrice;
  if (maxPrice !== undefined) state.maxPrice = maxPrice;
  const conditions = parseConditions(query);
  if (conditions) state.itemCondition = conditions;
  const group = positiveNumber(query.categoryGroupId);
  if (group !== undefined) state.categoryGroupId = group;
  if (isTrue(query.sold)) state.sold = true;
  if (isTrue(query.shippable)) state.shippable = true;
  if (isTrue(query.featured)) state.featured = true;
  if (isTrue(query.deals)) state.deals = true;
  if (isTrue(query.quality)) state.quality = true;
  const closed: Partial<Record<AttributeFilterName, readonly string[]>> = {
    gender: ATTRIBUTE_FILTER_VALUES.gender, ageGroup: ATTRIBUTE_FILTER_VALUES.ageGroup, color: ATTRIBUTE_FILTER_VALUES.color,
  };
  for (const key of ATTRIBUTE_FILTER_KEYS) {
    const list = parseList(query[key], closed[key]);
    if (list) state[key] = list;
  }
  const tf = encodeTagFacets(parseTagFacets(query.tf));
  if (tf) state.tf = tf;
  return state;
}

/** How many filters are set, category aside: what the Filter button's dot counts. */
export function activeFilterCount(state: FilterState): number {
  let n = 0;
  if (state.minPrice) n++;
  if (state.maxPrice) n++;
  if (state.itemCondition && state.itemCondition.length) n++;
  if (state.sold) n++;
  if (state.shippable) n++;
  if (state.featured) n++;
  if (state.deals) n++;
  if (state.quality) n++;
  for (const key of ATTRIBUTE_FILTER_KEYS) if (state[key]?.length) n++;
  return n + tagFacetCount(state.tf);
}

export const hasActiveFilters = (state: FilterState): boolean => activeFilterCount(state) > 0;

/**
 * The filters as URL query values: only what is set, lists as comma strings,
 * booleans as 'true'. Category group only when asked (the search page; a
 * browse page's category is its route).
 */
export function filtersToQuery(state: FilterState, options: { withCategoryGroup?: boolean } = {}): Record<string, string> {
  const q: Record<string, string> = {};
  if (state.minPrice) q.minPrice = String(state.minPrice);
  if (state.maxPrice) q.maxPrice = String(state.maxPrice);
  if (state.itemCondition && state.itemCondition.length) q.itemCondition = state.itemCondition.join(',');
  // Positive only, matching filtersFromQuery: -1 is the For You tab's marker, not a group.
  if (options.withCategoryGroup && state.categoryGroupId && state.categoryGroupId > 0) q.categoryGroupId = String(state.categoryGroupId);
  if (state.sold) q.sold = 'true';
  if (state.shippable) q.shippable = 'true';
  if (state.featured) q.featured = 'true';
  if (state.deals) q.deals = 'true';
  if (state.quality) q.quality = 'true';
  for (const key of ATTRIBUTE_FILTER_KEYS) {
    const list = state[key];
    if (list && list.length) q[key] = list.join(',');
  }
  if (state.tf) q.tf = state.tf;
  return q;
}

/** A copy of `params` with every filter key removed: the base a new filter set is applied on. */
export function withoutFilters<T extends Record<string, unknown>>(params: T): T {
  const out: Record<string, unknown> = { ...params };
  for (const key of FILTER_URL_KEYS) delete out[key];
  return out as T;
}

/** A stable identity for a filter set, for de-duplicating saved searches. Order of list values does not matter. */
export function filterKey(state: FilterState): string {
  const sorted: Record<string, unknown> = {};
  const query = filtersToQuery(state, { withCategoryGroup: true });
  for (const key of Object.keys(query).sort()) sorted[key] = query[key].split(',').sort().join(',');
  return JSON.stringify(sorted);
}
